/**
 * Chocowild homepage v3 interactions.
 * Content remains visible without JavaScript; motion and product switching are
 * progressive enhancements.
 * Supports Theme Editor reloads (shopify:section:load, shopify:block:select).
 */
(() => {
  const initializedAttribute = "data-cwh-initialized";
  document.documentElement.classList.add("cwh-motion-ready");
  const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const precisePointerQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
  const motionSectionSelector = [
    ".cwh-hero",
    ".cwh-proof",
    ".cwh-products",
    ".cwh-flight",
    ".cwh-scenes",
    ".cw-lifestyle-campaign",
    ".cw-ingredients-campaign",
    ".cwh-moments",
    ".cwh-story",
    ".cwh-ritual",
    ".cwh-faq",
    ".cwh-newsletter"
  ].join(",");
  const motionHeadingSelector = [
    ".cwh-hero__heading",
    ".cwh-flight__heading",
    ".cw-heading",
    ".cw-lifestyle-campaign__heading",
    ".cw-ingredients-campaign__heading",
    ".cwh-story__heading",
    ".cwh-newsletter__heading"
  ].join(",");
  let scrollMotionObserver;
  let typingHeadingObserver;
  let detailMotionObserver;
  let passedMotionFrame = 0;
  let passedMotionListenerAttached = false;

  const readMotionValue = (property, fallback) => {
    const value = Number.parseFloat(
      window.getComputedStyle(document.documentElement).getPropertyValue(property)
    );
    return Number.isFinite(value) ? value : fallback;
  };

  /**
   * Prevent duplicate listeners during Shopify Theme Editor reloads.
   */
  const claimModule = (element) => {
    if (!element || element.hasAttribute(initializedAttribute)) return false;
    element.setAttribute(initializedAttribute, "true");
    return true;
  };

  /**
   * Reveal elements only after the motion-ready class has been applied.
   */
  const initializeReveals = (scope) => {
    if (typeof window.cwInitReveal === 'function') {
      window.cwInitReveal(scope);
      return;
    }
    const elements = scope.querySelectorAll("[data-cwh-reveal]");
    elements.forEach((element) => {
      element.classList.add("is-visible");
      delete element.dataset.cwRevealActive;
    });
  };

  /**
   * Split major headings into accessible word units. Text remains in the DOM
   * and the original string is exposed through aria-label.
   */
  const prepareTypingHeading = (heading) => {
    if (!heading || heading.dataset.cwhTypeReady) return;

    const accessibleLabel = heading.textContent.replace(/\s+/g, " ").trim();
    if (!accessibleLabel) return;

    let characterIndex = 0;
    const walker = document.createTreeWalker(
      heading,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) =>
          node.textContent.trim()
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT
      }
    );
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((textNode) => {
      const fragment = document.createDocumentFragment();
      textNode.textContent.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          fragment.appendChild(document.createTextNode(part));
          return;
        }

        const word = document.createElement("span");
        word.className = "cwh-type-word";
        word.setAttribute("aria-hidden", "true");

        Array.from(part).forEach((character) => {
          const characterNode = document.createElement("span");
          characterNode.className = "cwh-type-char";
          characterNode.style.setProperty(
            "--cwh-character-index",
            String(characterIndex)
          );
          characterNode.textContent = character;
          word.appendChild(characterNode);
          characterIndex += 1;
        });

        fragment.appendChild(word);
      });
      textNode.replaceWith(fragment);
    });

    heading.dataset.cwhTypeReady = "true";
    heading.dataset.cwhCharacterCount = String(characterIndex);
    heading.classList.add("cwh-type-heading");
    heading.setAttribute("aria-label", accessibleLabel);
  };

  const finishTypingHeading = (heading) => {
    if (
      !heading ||
      heading.classList.contains("is-cwh-typing-started")
    ) {
      return;
    }
    const characterCount = Number(heading.dataset.cwhCharacterCount || 1);
    heading.classList.add("is-cwh-typing-started");
    heading.classList.add("is-cwh-typing");
    const characterDelay = readMotionValue("--cw-typing-delay", 45);
    window.setTimeout(() => {
      heading.classList.remove("is-cwh-typing");
      heading.classList.add("is-cwh-typed");
    }, Math.min(5600, characterCount * characterDelay + 520));
  };

  /**
   * Progressive cinematic motion. CSS is opt-in through cwh-scroll-motion, so
   * all content remains visible when JavaScript or IntersectionObserver fails.
   */
  const prepareMotionSection = (section) => {
    if (!section || section.dataset.cwhMotionInitialized) return;
    section.dataset.cwhMotionInitialized = "true";
    section.dataset.cwhMotionSection = "true";

    if (document.body?.dataset.cwTyping !== "false") {
      section.querySelectorAll(motionHeadingSelector).forEach(prepareTypingHeading);
    }

    section
      .querySelectorAll(
        [
          ".cwh-proof__item",
          ".cwh-product-card",
          ".cwh-scene",
          ".cwh-moment-card",
          ".cwh-story__ingredients > div",
          ".cwh-ritual-step",
          ".cwh-faq-item"
        ].join(",")
      )
      .forEach((item, index) => {
        item.dataset.cwhStaggerItem = "true";
        item.style.setProperty("--cwh-stagger-index", String(Math.min(index, 7)));
      });

    section
      .querySelectorAll(
        [
          ".cwh-hero-product__image",
          ".cwh-product-card__image",
          ".cwh-scene__media",
          ".cw-lifestyle-campaign__media",
          ".cw-ingredients-campaign__media",
          ".cwh-moment-card__image",
          ".cwh-story__media",
          ".cwh-ritual__media"
        ].join(",")
      )
      .forEach((media) => {
        media.dataset.cwhMediaReveal = "true";
      });

    section
      .querySelectorAll(
        [
          ".cw-eyebrow",
          ".cwh-hero__lede",
          ".cwh-hero__actions",
          ".cwh-scenes__intro",
          ".cw-lifestyle-campaign__text",
          ".cw-lifestyle-campaign__copy .cw-button",
          ".cw-ingredients-campaign__text",
          ".cw-ingredients-campaign__copy .cw-text-link",
          ".cwh-moments__intro",
          ".cwh-story__text",
          ".cwh-ritual__intro",
          ".cwh-faq__text",
          ".cwh-newsletter__content"
        ].join(",")
      )
      .forEach((copy, index) => {
        copy.dataset.cwhCopyReveal = "true";
        copy.style.setProperty("--cwh-copy-index", String(Math.min(index, 4)));
      });
  };

  const activateMotionSection = (section) => {
    section.classList.add("is-cwh-inview");

    window.setTimeout(() => {
      section.classList.add("is-cwh-motion-settled");
    }, 1350);
  };

  const getMotionSections = (scope) => {
    const sections = [...scope.querySelectorAll(motionSectionSelector)];
    if (scope.nodeType === 1 && scope.matches(motionSectionSelector)) {
      sections.unshift(scope);
    }
    return [...new Set(sections)];
  };

  const initializeScrollMotion = (scope = document) => {
    const sections = getMotionSections(scope);
    if (!sections.length) return;

    sections.forEach(prepareMotionSection);

    const shouldRevealImmediately =
      motionQuery.matches ||
      document.body?.dataset.cwMotion === "false" ||
      document.body?.dataset.cwRevealEnabled === "false" ||
      window.Shopify?.designMode ||
      !("IntersectionObserver" in window);

    if (shouldRevealImmediately) {
      sections.forEach((section) => {
        activateMotionSection(section);
        section
          .querySelectorAll("[data-cwh-type-ready]")
          .forEach(finishTypingHeading);
        section
          .querySelectorAll("[data-cwh-detail-motion]")
          .forEach((detail) => detail.classList.add("is-cwh-detail-inview"));
      });
      return;
    }

    if (!scrollMotionObserver) {
      scrollMotionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            activateMotionSection(entry.target);
            scrollMotionObserver.unobserve(entry.target);
          });
        },
        {
          rootMargin: "0px 0px 12% 0px",
          threshold: 0.08
        }
      );
    }

    if (!typingHeadingObserver) {
      typingHeadingObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            finishTypingHeading(entry.target);
            typingHeadingObserver.unobserve(entry.target);
          });
        },
        {
          rootMargin: "0px 0px -28% 0px",
          threshold: 0.2
        }
      );
    }

    if (!detailMotionObserver) {
      detailMotionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-cwh-detail-inview");
            detailMotionObserver.unobserve(entry.target);
          });
        },
        {
          rootMargin: "0px 0px -14% 0px",
          threshold: 0.16
        }
      );
    }

    document.documentElement.classList.add("cwh-scroll-motion");
    sections.forEach((section) => scrollMotionObserver.observe(section));
    sections.forEach((section) => {
      section.querySelectorAll("[data-cwh-type-ready]").forEach((heading) => {
        if (heading.dataset.cwhTypeObserved) return;
        heading.dataset.cwhTypeObserved = "true";
        typingHeadingObserver.observe(heading);
      });
      section.querySelectorAll("[data-cwh-detail-motion]").forEach((detail) => {
        if (detail.dataset.cwhDetailObserved) return;
        detail.dataset.cwhDetailObserved = "true";
        detailMotionObserver.observe(detail);
      });
    });

    let visibleMotionTimer = 0;
    const activateVisibleMotion = () => {
      if (visibleMotionTimer) return;
      const run = () => {
        visibleMotionTimer = 0;
        const viewportTop = -Math.round(window.innerHeight * 0.12);
        const viewportBottom = window.innerHeight * 1.12;
        sections.forEach((section) => {
          if (section.classList.contains("is-cwh-inview")) return;
          const rect = section.getBoundingClientRect();
          if (rect.top <= viewportBottom && rect.bottom >= viewportTop) {
            activateMotionSection(section);
          }
        });
        sections.forEach((section) => {
          section.querySelectorAll("[data-cwh-type-ready]").forEach((heading) => {
            if (heading.classList.contains("is-cwh-typing-started")) return;
            const rect = heading.getBoundingClientRect();
            if (rect.top <= viewportBottom && rect.bottom >= viewportTop) {
              finishTypingHeading(heading);
            }
          });
        });
      };
      visibleMotionTimer = window.setTimeout(run, 16);
    };

    window.addEventListener("scroll", activateVisibleMotion, { passive: true });
    window.addEventListener("resize", activateVisibleMotion, { passive: true });
    activateVisibleMotion();

    const revealPassedMotionSections = () => {
      if (passedMotionFrame) return;
      passedMotionFrame = window.requestAnimationFrame(() => {
        passedMotionFrame = 0;
        getMotionSections(document).forEach((section) => {
          if (section.classList.contains("is-cwh-inview")) return;
          if (section.getBoundingClientRect().bottom <= 0) {
            activateMotionSection(section);
            section
              .querySelectorAll("[data-cwh-type-ready]")
              .forEach(finishTypingHeading);
            section
              .querySelectorAll("[data-cwh-detail-motion]")
              .forEach((detail) => detail.classList.add("is-cwh-detail-inview"));
            scrollMotionObserver.unobserve(section);
          }
        });
      });
    };

    if (!passedMotionListenerAttached) {
      passedMotionListenerAttached = true;
      window.addEventListener("scroll", revealPassedMotionSections, { passive: true });
      window.addEventListener("resize", revealPassedMotionSections, { passive: true });
    }
    revealPassedMotionSections();
  };

  /**
   * Switch the product presented in the split hero.
   */
  const initializeHeroGallery = (gallery) => {
    if (!claimModule(gallery)) return;

    const triggers = [...gallery.querySelectorAll("[data-cwh-hero-trigger]")];
    const panels = [...gallery.querySelectorAll("[data-cwh-hero-panel]")];
    if (triggers.length === 0 || panels.length === 0) return;

    const selectProduct = (selectedIndex) => {
      triggers.forEach((trigger) => {
        const isSelected = trigger.dataset.cwhHeroTrigger === String(selectedIndex);
        trigger.classList.toggle("is-active", isSelected);
        trigger.setAttribute("aria-selected", String(isSelected));
      });

      panels.forEach((panel) => {
        const isSelected = panel.dataset.cwhHeroPanel === String(selectedIndex);
        panel.hidden = !isSelected;
        panel.classList.toggle("is-active", isSelected);
      });
    };

    gallery.addEventListener("click", (event) => {
      const trigger = event.target.closest("[data-cwh-hero-trigger]");
      if (!trigger || !gallery.contains(trigger)) return;
      selectProduct(trigger.dataset.cwhHeroTrigger);
    });

    gallery.addEventListener("keydown", (event) => {
      if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;

      const currentIndex = triggers.indexOf(document.activeElement);
      if (currentIndex < 0) return;

      event.preventDefault();
      const direction = event.key === "ArrowRight" ? 1 : -1;
      const nextIndex = (currentIndex + direction + triggers.length) % triggers.length;
      triggers[nextIndex].focus();
      selectProduct(triggers[nextIndex].dataset.cwhHeroTrigger);
    });
  };

  /**
   * Give owned chocolate cut-outs a subtle cursor-following depth effect.
   * The artwork stays in its authored CSS position when motion is unavailable.
   */
  const initializeParallax = (stage) => {
    if (
      !stage ||
      stage.hasAttribute("data-cwh-parallax-initialized") ||
      motionQuery.matches ||
      document.body?.dataset.cwHoverMotion === "false" ||
      !precisePointerQuery.matches
    ) {
      return;
    }

    const pieces = [...stage.querySelectorAll("[data-cwh-float]")];
    if (pieces.length === 0) return;

    stage.setAttribute("data-cwh-parallax-initialized", "true");
    let animationFrame = 0;
    let pointerEvent;

    const render = () => {
      if (!pointerEvent || motionQuery.matches) return;
      const bounds = stage.getBoundingClientRect();
      if (bounds.width === 0 || bounds.height === 0) return;

      const x = ((pointerEvent.clientX - bounds.left) / bounds.width - 0.5) * 2;
      const y = ((pointerEvent.clientY - bounds.top) / bounds.height - 0.5) * 2;

      pieces.forEach((piece) => {
        const depth = Number(piece.dataset.cwhDepth || 12);
        piece.style.setProperty("--cwh-x", `${(x * depth).toFixed(2)}px`);
        piece.style.setProperty("--cwh-y", `${(y * depth * 0.7).toFixed(2)}px`);
      });

      animationFrame = 0;
    };

    stage.addEventListener("pointermove", (event) => {
      if (motionQuery.matches) return;
      pointerEvent = event;
      if (!animationFrame) animationFrame = window.requestAnimationFrame(render);
    });

    stage.addEventListener("pointerleave", () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      animationFrame = 0;
      pieces.forEach((piece) => {
        piece.style.removeProperty("--cwh-x");
        piece.style.removeProperty("--cwh-y");
      });
    });
  };

  /**
   * Add restrained perspective to the flavor worlds without blocking links.
   */
  const initializeTiltCard = (card) => {
    if (
      !card ||
      card.hasAttribute("data-cwh-tilt-initialized") ||
      motionQuery.matches ||
      document.body?.dataset.cwHoverMotion === "false" ||
      !precisePointerQuery.matches
    ) {
      return;
    }

    card.setAttribute("data-cwh-tilt-initialized", "true");

    card.addEventListener("pointermove", (event) => {
      if (motionQuery.matches) return;
      const bounds = card.getBoundingClientRect();
      if (bounds.width === 0 || bounds.height === 0) return;

      const x = (event.clientX - bounds.left) / bounds.width - 0.5;
      const y = (event.clientY - bounds.top) / bounds.height - 0.5;
      card.style.setProperty("--cwh-tilt-x", `${(-y * 4).toFixed(2)}deg`);
      card.style.setProperty("--cwh-tilt-y", `${(x * 4).toFixed(2)}deg`);
    });

    card.addEventListener("pointerleave", () => {
      card.style.removeProperty("--cwh-tilt-x");
      card.style.removeProperty("--cwh-tilt-y");
    });
  };

  /**
   * Variant-safe Ajax add for products with only a default variant.
   */
  const updateHeaderCartCount = (itemCount) => {
    document
      .querySelectorAll(".cw-site-header a[href*='/cart']")
      .forEach((cartLink) => {
        cartLink.setAttribute(
          "aria-label",
          window.cwI18n?.("cart_label", { count: itemCount }) || `Cart, ${itemCount} items`
        );
        let count = cartLink.querySelector(".cw-cart-count");

        if (itemCount > 0 && !count) {
          count = document.createElement("span");
          count.className = "cw-cart-count";
          count.setAttribute("aria-hidden", "true");
          cartLink.appendChild(count);
        }

        if (count) {
          count.textContent = String(itemCount);
          count.hidden = itemCount === 0;
        }
      });
  };

  const playAddToCartFeedback = (form, button) => {
    form.classList.add("is-added");

    const cartLink = document.querySelector(".cw-site-header a[href*='/cart']");
    if (cartLink) {
      cartLink.classList.remove("is-cwh-cart-bumped");
      window.requestAnimationFrame(() => {
        cartLink.classList.add("is-cwh-cart-bumped");
      });
      window.setTimeout(() => {
        cartLink.classList.remove("is-cwh-cart-bumped");
      }, readMotionValue("--cw-cart-animation-speed", 750));
    }

    const reduceMotion =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      document.body?.dataset.cwMotion === "false";
    if (reduceMotion || !cartLink || typeof button.animate !== "function") return;

    const panel = form.closest(".cwh-hero-product");
    const productImage = panel?.querySelector(".cwh-hero-product__image img");
    const sourceRect = productImage?.getBoundingClientRect() || button.getBoundingClientRect();
    const targetRect = cartLink.getBoundingClientRect();
    const size = 54;
    const startX = sourceRect.left + sourceRect.width / 2 - size / 2;
    const startY = sourceRect.top + sourceRect.height / 2 - size / 2;
    const endX = targetRect.left + targetRect.width / 2 - size / 2;
    const endY = targetRect.top + targetRect.height / 2 - size / 2;
    const flyer = document.createElement("span");

    flyer.className = "cwh-cart-flyer";
    flyer.style.left = `${startX}px`;
    flyer.style.top = `${startY}px`;
    flyer.style.width = `${size}px`;
    flyer.style.height = `${size}px`;
    if (productImage?.currentSrc || productImage?.src) {
      flyer.style.backgroundImage = `url("${productImage.currentSrc || productImage.src}")`;
    }
    document.body.appendChild(flyer);

    const animation = flyer.animate(
      [
        { opacity: 0, transform: "translate3d(0, 0, 0) scale(0.55)" },
        { opacity: 1, offset: 0.16, transform: "translate3d(0, -18px, 0) scale(1)" },
        {
          opacity: 0.95,
          offset: 0.72,
          transform: `translate3d(${(endX - startX) * 0.72}px, ${(endY - startY) * 0.68 - 30}px, 0) scale(0.62)`
        },
        {
          opacity: 0,
          transform: `translate3d(${endX - startX}px, ${endY - startY}px, 0) scale(0.18)`
        }
      ],
      {
        duration: readMotionValue("--cw-cart-animation-speed", 750),
        easing:
          getComputedStyle(document.documentElement)
            .getPropertyValue("--cw-ease")
            .trim() || "cubic-bezier(0.22, 0.8, 0.25, 1)",
        fill: "forwards"
      }
    );

    animation.finished.finally(() => flyer.remove());
  };

  const initializeProductForm = (form) => {
    if (!claimModule(form)) return;

    form.addEventListener("submit", async (event) => {
      if (!window.fetch || !window.FormData) return;

      event.preventDefault();
      const button = form.querySelector("button[type='submit']");
      const label = form.querySelector("[data-cwh-button-label]");
      if (!button || !label) return;

      const originalLabel = label.textContent;
      button.disabled = true;
      button.setAttribute("aria-busy", "true");

      try {
        const shopRoot =
          window.Shopify && window.Shopify.routes
            ? window.Shopify.routes.root
            : "/";

        const response = await fetch(`${shopRoot}cart/add.js`, {
          method: "POST",
          headers: {
            Accept: "application/json"
          },
          body: new FormData(form)
        });

        if (!response.ok) throw new Error("Unable to add product");

        const cartResponse = await fetch(`${shopRoot}cart.js`, {
          headers: {
            Accept: "application/json"
          }
        });
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          updateHeaderCartCount(cart.item_count);
        }

        label.textContent = window.cwI18n?.("added") || "Added";
        playAddToCartFeedback(form, button);
        document.dispatchEvent(new CustomEvent("cw:cart-updated"));
      } catch (error) {
        form.submit();
        return;
      }

      window.setTimeout(() => {
        button.disabled = false;
        button.removeAttribute("aria-busy");
        label.textContent = originalLabel;
        form.classList.remove("is-added");
      }, 1400);
    });
  };

  /**
   * Focus-center carousel for editorial card groups on compact screens.
   * It keeps one complete card readable while the adjacent cards remain
   * visible as smaller, softened previews. Desktop keeps the authored grid.
   */
  const initializeFocusCarousel = (carousel) => {
    if (!carousel || carousel.dataset.cwhFocusCarouselReady) return;

    const cards = [...carousel.querySelectorAll("[data-cwh-carousel-card]")];
    if (cards.length < 2) return;

    carousel.dataset.cwhFocusCarouselReady = "true";
    carousel.classList.add("cwh-focus-carousel-ready");
    carousel.closest(".cw-story-grid, .cw-contact-directory")?.classList.add("cwh-focus-carousel-host");

    const compactQuery = window.matchMedia("(max-width: 989px)");
    let activeIndex = 0;
    let pointerStart = null;
    let suppressClickUntil = 0;

    const controls = document.createElement("div");
    controls.className = "cwh-focus-carousel__controls";
    controls.innerHTML = `
      <button class="cwh-focus-carousel__arrow" type="button" data-cwh-carousel-previous aria-label="Previous card">
        <span aria-hidden="true">←</span>
      </button>
      <div class="cwh-focus-carousel__dots" role="group" aria-label="Choose card"></div>
      <button class="cwh-focus-carousel__arrow" type="button" data-cwh-carousel-next aria-label="Next card">
        <span aria-hidden="true">→</span>
      </button>
      <span class="cw-visually-hidden" data-cwh-carousel-status aria-live="polite"></span>
    `;
    carousel.insertAdjacentElement("afterend", controls);

    const dotList = controls.querySelector("[data-cwh-carousel-status]")
      ? controls.querySelector(".cwh-focus-carousel__dots")
      : null;
    const status = controls.querySelector("[data-cwh-carousel-status]");
    const dots = cards.map((card, index) => {
      const dot = document.createElement("button");
      dot.className = "cwh-focus-carousel__dot";
      dot.type = "button";
      dot.setAttribute("aria-label", `Show card ${index + 1} of ${cards.length}`);
      dot.addEventListener("click", () => setActive(index, true));
      dotList?.appendChild(dot);
      return dot;
    });

    const normalizeIndex = (index) => (index + cards.length) % cards.length;

    const setDesktopState = () => {
      cards.forEach((card) => {
        card.classList.remove(
          "is-cwh-carousel-active",
          "is-cwh-carousel-previous",
          "is-cwh-carousel-next"
        );
        card.removeAttribute("aria-current");
        card.removeAttribute("aria-hidden");
        card.querySelectorAll("a, button").forEach((interactive) => {
          if (!interactive.hasAttribute("data-cwh-original-tabindex")) {
            interactive.removeAttribute("tabindex");
          }
        });
      });
    };

    function setActive(index, announce = false) {
      activeIndex = normalizeIndex(index);

      if (!compactQuery.matches) {
        setDesktopState();
        return;
      }

      const previousIndex = normalizeIndex(activeIndex - 1);
      const nextIndex = normalizeIndex(activeIndex + 1);

      cards.forEach((card, cardIndex) => {
        const isActive = cardIndex === activeIndex;
        const isPrevious = cardIndex === previousIndex;
        const isNext = cardIndex === nextIndex;

        card.classList.toggle("is-cwh-carousel-active", isActive);
        card.classList.toggle("is-cwh-carousel-previous", isPrevious);
        card.classList.toggle("is-cwh-carousel-next", isNext);
        card.toggleAttribute("aria-current", isActive);
        card.setAttribute("aria-hidden", String(!isActive));

        card.querySelectorAll("a, button").forEach((interactive) => {
          if (isActive) {
            interactive.removeAttribute("tabindex");
          } else {
            interactive.setAttribute("tabindex", "-1");
          }
        });
      });

      dots.forEach((dot, dotIndex) => {
        const isCurrent = dotIndex === activeIndex;
        dot.classList.toggle("is-active", isCurrent);
        dot.setAttribute("aria-current", isCurrent ? "true" : "false");
      });

      if (status && announce) {
        status.textContent = `Card ${activeIndex + 1} of ${cards.length}`;
      }
    }

    controls
      .querySelector("[data-cwh-carousel-previous]")
      ?.addEventListener("click", () => setActive(activeIndex - 1, true));
    controls
      .querySelector("[data-cwh-carousel-next]")
      ?.addEventListener("click", () => setActive(activeIndex + 1, true));

    carousel.addEventListener("click", (event) => {
      if (!compactQuery.matches) return;
      if (performance.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        suppressClickUntil = 0;
        return;
      }

      const card = event.target.closest("[data-cwh-carousel-card]");
      if (!card || !carousel.contains(card)) return;

      const selectedIndex = cards.indexOf(card);
      if (selectedIndex === activeIndex) return;

      event.preventDefault();
      setActive(selectedIndex, true);
    });

    carousel.addEventListener("keydown", (event) => {
      if (!compactQuery.matches || !["ArrowLeft", "ArrowRight"].includes(event.key)) {
        return;
      }

      event.preventDefault();
      setActive(activeIndex + (event.key === "ArrowRight" ? 1 : -1), true);
    });

    carousel.addEventListener("pointerdown", (event) => {
      if (!compactQuery.matches || event.pointerType === "mouse") return;
      pointerStart = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY
      };
      carousel.setPointerCapture?.(event.pointerId);
    });

    carousel.addEventListener("pointerup", (event) => {
      if (
        !compactQuery.matches ||
        !pointerStart ||
        (pointerStart.id !== undefined && pointerStart.id !== event.pointerId)
      ) {
        return;
      }

      const deltaX = event.clientX - pointerStart.x;
      const deltaY = event.clientY - pointerStart.y;
      pointerStart = null;

      if (Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      suppressClickUntil = performance.now() + 420;
      setActive(activeIndex + (deltaX < 0 ? 1 : -1), true);
    });

    carousel.addEventListener("pointercancel", () => {
      pointerStart = null;
    });

    compactQuery.addEventListener?.("change", () => setActive(activeIndex));
    setActive(0);
  };

  /**
   * Condense the three-step ritual into a keyboard-friendly tab switcher on
   * phones while retaining the authored three-column/stacked presentation
   * above the compact breakpoint.
   */
  const initializeRitualTabs = (ritual) => {
    if (!ritual || ritual.dataset.cwhRitualReady) return;

    const tabs = [...ritual.querySelectorAll("[data-cwh-ritual-tab]")];
    const panels = [...ritual.querySelectorAll("[data-cwh-ritual-panel]")];
    if (tabs.length < 2 || tabs.length !== panels.length) return;

    ritual.dataset.cwhRitualReady = "true";
    const compactQuery = window.matchMedia("(max-width: 749px)");
    let activeIndex = 0;

    const normalizeIndex = (index) => (index + tabs.length) % tabs.length;

    const setActive = (index, focusTab = false) => {
      activeIndex = normalizeIndex(index);

      tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === activeIndex;
        tab.setAttribute("aria-selected", String(isActive));
        tab.setAttribute("tabindex", isActive ? "0" : "-1");
        if (focusTab && isActive) tab.focus();
      });

      panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === activeIndex;
        panel.classList.toggle("is-cwh-ritual-active", isActive);
        panel.setAttribute("aria-hidden", compactQuery.matches ? String(!isActive) : "false");
      });
    };

    tabs.forEach((tab, tabIndex) => {
      tab.addEventListener("click", () => setActive(tabIndex));
      tab.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;

        event.preventDefault();
        const nextIndex =
          event.key === "Home"
            ? 0
            : event.key === "End"
              ? tabs.length - 1
              : activeIndex + (event.key === "ArrowRight" ? 1 : -1);
        setActive(nextIndex, true);
      });
    });

    compactQuery.addEventListener?.("change", () => setActive(activeIndex));
    setActive(0);
  };

  /**
   * Keyboard scroll support for horizontal rails.
   */
  const initializeRailScroll = (rail) => {
    if (!claimModule(rail)) return;

    rail.addEventListener("keydown", (event) => {
      if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
      const scrollAmount = rail.clientWidth * 0.75;
      if (event.key === "ArrowRight") {
        rail.scrollBy({ left: scrollAmount, behavior: "smooth" });
      } else {
        rail.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      }
    });
  };

  /**
   * Let compact campaign copy slide aside so the photography can be viewed
   * unobstructed. The small edge tab remains visible and accessible.
   */
  const initializeCampaignPanel = (panel) => {
    if (!panel || panel.dataset.cwCampaignPanelReady) return;

    const toggle = panel.querySelector("[data-cw-campaign-toggle]");
    if (!toggle) return;

    panel.dataset.cwCampaignPanelReady = "true";
    const label = toggle.querySelector("[data-cw-campaign-toggle-label]");
    const stage = panel.closest(".cw-lifestyle-campaign, .cw-ingredients-campaign");

    const setPanelState = (collapsed) => {
      panel.classList.toggle("is-collapsed", collapsed);
      toggle.classList.toggle("is-collapsed", collapsed);
      toggle.dataset.cwCampaignCollapsed = String(collapsed);
      toggle.setAttribute("aria-expanded", String(!collapsed));
      toggle.setAttribute(
        "aria-label",
        collapsed
          ? window.cwI18n?.("show_story") || "Show story"
          : window.cwI18n?.("hide_story") || "Hide story"
      );
      if (label) {
        label.textContent = collapsed
          ? window.cwI18n?.("show_story") || "Show story"
          : window.cwI18n?.("hide_story") || "Hide story";
      }
    };

    // Normalize the initial server-rendered state as well as click/swipe
    // changes so the visible icon, ARIA label, and panel class never diverge.
    setPanelState(panel.classList.contains("is-collapsed"));

    toggle.addEventListener("click", () => {
      if (panel.dataset.cwCampaignSuppressClick === "true") {
        panel.dataset.cwCampaignSuppressClick = "false";
        return;
      }
      setPanelState(!panel.classList.contains("is-collapsed"));
    });

    // Touch users can swipe from any non-interactive point on the campaign,
    // not only the narrow edge tab. Vertical page scrolling remains native.
    if (!stage) return;
    let gesture = null;

    const clearGesture = () => {
      if (!gesture) return;
      try {
        stage.releasePointerCapture(gesture.pointerId);
      } catch (error) {
        // Pointer capture may already have been released by the browser.
      }
      panel.style.removeProperty("transform");
      panel.style.removeProperty("transition");
      gesture = null;
    };

    stage.addEventListener("pointerdown", (event) => {
      if (!(event.pointerType === "touch" || event.pointerType === "pen")) return;
      if (event.target.closest("a, button, input, select, textarea")) return;

      gesture = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        collapsed: panel.classList.contains("is-collapsed"),
        active: false,
      };
      try {
        stage.setPointerCapture(event.pointerId);
      } catch (error) {
        // Continue without capture on browsers that do not support it.
      }
    });

    stage.addEventListener("pointermove", (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      const deltaX = event.clientX - gesture.startX;
      const deltaY = event.clientY - gesture.startY;

      if (!gesture.active) {
        if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) return;
        if (Math.abs(deltaY) >= Math.abs(deltaX)) {
          clearGesture();
          return;
        }
        if ((!gesture.collapsed && deltaX >= 0) || (gesture.collapsed && deltaX <= 0)) {
          clearGesture();
          return;
        }
        gesture.active = true;
        panel.dataset.cwCampaignSuppressClick = "true";
        panel.style.setProperty("transition", "none", "important");
      }

      event.preventDefault();
      const width = panel.getBoundingClientRect().width || stage.clientWidth;
      const offset = gesture.collapsed
        ? Math.min(0, Math.max(-width, -width + deltaX))
        : Math.min(0, Math.max(-width, deltaX));
      panel.style.setProperty("transform", `translateX(${offset}px)`, "important");
    }, { passive: false });

    stage.addEventListener("pointerup", (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      if (!gesture.active) {
        clearGesture();
        return;
      }

      const width = panel.getBoundingClientRect().width || stage.clientWidth;
      const deltaX = event.clientX - gesture.startX;
      const progress = Math.min(1, Math.abs(deltaX) / width);
      const shouldCollapse = gesture.collapsed ? progress < 0.45 : progress >= 0.45;
      const wasCollapsed = gesture.collapsed;

      panel.style.removeProperty("transform");
      panel.style.removeProperty("transition");
      setPanelState(shouldCollapse);
      if (wasCollapsed !== shouldCollapse) panel.dataset.cwCampaignSuppressClick = "true";
      clearGesture();
    });

    stage.addEventListener("pointercancel", clearGesture);
  };

  /**
   * Give homepage campaign wrappers an explicit scroll range for sticky
   * stages. Shopify's section shell is a grid by default and an auto-height
   * wrapper with only bottom padding is not a reliable sticky containing
   * block in Chromium. Measuring the authored stage keeps compact/default/tall
   * section settings in sync without duplicating their values in CSS. The
   * CSS pin space is an intentional pause: it keeps the next section below
   * the viewport for one beat before that section starts to cover the stage.
   */
  const campaignPinQuery = window.matchMedia("(min-width: 1200px)");
  const pinnedStageSelector = "[data-cw-pin-stage]";

  const syncCampaignPin = (campaign) => {
    if (!campaign || !campaignPinQuery.matches) return;

    const wrapper = campaign.parentElement;
    if (!wrapper?.matches("main > .shopify-section")) return;

    const stageHeight = campaign.getBoundingClientRect().height;
    if (!stageHeight) return;

    if (!wrapper.dataset.cwCampaignPinOriginalHeightCaptured) {
      wrapper.dataset.cwCampaignPinOriginalHeightCaptured = "true";
      wrapper.dataset.cwCampaignPinOriginalHeight = wrapper.style.height;
      wrapper.dataset.cwCampaignPinOriginalPaddingBottom =
        wrapper.style.paddingBottom;
    }

    wrapper.style.height = `calc(${Math.ceil(stageHeight * 2)}px + var(--cw-campaign-pin-space, 0px))`;
    wrapper.style.paddingBottom = "0px";

    const next = wrapper.nextElementSibling;
    if (!next) return;
    if (!next.dataset.cwCampaignPinOriginalMarginCaptured) {
      next.dataset.cwCampaignPinOriginalMarginCaptured = "true";
      next.dataset.cwCampaignPinOriginalMarginTop = next.style.marginTop;
    }
    next.style.marginTop = `-${Math.ceil(stageHeight)}px`;
  };

  const clearCampaignPin = (wrapper) => {
    if (!wrapper) return;

    if (wrapper.dataset.cwCampaignPinOriginalHeightCaptured) {
      if (wrapper.dataset.cwCampaignPinOriginalHeight) {
        wrapper.style.height = wrapper.dataset.cwCampaignPinOriginalHeight;
      } else {
        wrapper.style.removeProperty("height");
      }
      if (wrapper.dataset.cwCampaignPinOriginalPaddingBottom) {
        wrapper.style.paddingBottom =
          wrapper.dataset.cwCampaignPinOriginalPaddingBottom;
      } else {
        wrapper.style.removeProperty("padding-bottom");
      }
    }

    const next = wrapper.nextElementSibling;
    if (!next || !next.dataset.cwCampaignPinOriginalMarginCaptured) return;
    if (next.dataset.cwCampaignPinOriginalMarginTop) {
      next.style.marginTop = next.dataset.cwCampaignPinOriginalMarginTop;
    } else {
      next.style.removeProperty("margin-top");
    }
  };

  const initializeCampaignPinning = (scope = document) => {
    const campaigns = [
      ...(scope.matches?.(pinnedStageSelector)
        ? [scope]
        : []),
      ...(scope.querySelectorAll?.(pinnedStageSelector) || []),
    ];

    [...new Set(campaigns)].forEach((campaign) => {
      const wrapper = campaign.parentElement;
      if (!wrapper?.matches("main > .shopify-section")) return;
      if (campaign.dataset.cwCampaignPinReady) {
        syncCampaignPin(campaign);
        return;
      }

      campaign.dataset.cwCampaignPinReady = "true";
      const update = () => {
        if (campaignPinQuery.matches) syncCampaignPin(campaign);
        else clearCampaignPin(wrapper);
      };

      if ("ResizeObserver" in window) {
        const observer = new ResizeObserver(update);
        observer.observe(campaign);
        campaign._cwCampaignPinObserver = observer;
      }

      update();
    });
  };

  /**
   * Initialize a full document or a section inserted by the Theme Editor.
   */
  const initializeHomepage = (scope = document) => {
    initializeCampaignPinning(scope);
    initializeReveals(scope);
    initializeScrollMotion(scope);
    scope.querySelectorAll("[data-cwh-hero-gallery]").forEach(initializeHeroGallery);
    scope.querySelectorAll("[data-cwh-parallax]").forEach(initializeParallax);
    scope.querySelectorAll("[data-cwh-tilt]").forEach(initializeTiltCard);
    scope.querySelectorAll("[data-cwh-product-form]").forEach(initializeProductForm);
    scope
      .querySelectorAll("[data-cwh-focus-carousel]")
      .forEach(initializeFocusCarousel);
    scope.querySelectorAll("[data-cwh-ritual]").forEach(initializeRitualTabs);
    scope.querySelectorAll("[data-cwh-rail]").forEach(initializeRailScroll);
    scope
      .querySelectorAll("[data-cw-campaign-panel]")
      .forEach(initializeCampaignPanel);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeHomepage());
  } else {
    initializeHomepage();
  }

  // Handle Theme Editor reloads seamlessly
  document.addEventListener("shopify:section:load", (event) => {
    initializeHomepage(event.target);
  });

  // Handle Theme Editor block selection
  document.addEventListener("shopify:block:select", (event) => {
    const block = event.target;
    if (!block) return;

    if (block.matches("details.cwh-faq-item")) {
      block.open = true;
    }

    if (block.hasAttribute("data-cwh-hero-panel")) {
      const gallery = block.closest("[data-cwh-hero-gallery]");
      if (gallery) {
        const index = block.dataset.cwhHeroPanel;
        const trigger = gallery.querySelector(`[data-cwh-hero-trigger="${index}"]`);
        if (trigger) trigger.click();
      }
    }
  });

  // Dynamically respond to prefers-reduced-motion state changes
  motionQuery.addEventListener?.("change", () => {
    if (motionQuery.matches) {
      document.documentElement.classList.remove("cwh-scroll-motion");
      document.querySelectorAll("[data-cwh-motion-section]").forEach(activateMotionSection);
      document.querySelectorAll("[data-cwh-detail-motion]").forEach((detail) => {
        detail.classList.add("is-cwh-detail-inview");
      });
      document.querySelectorAll("[data-cwh-float]").forEach((piece) => {
        piece.style.removeProperty("--cwh-x");
        piece.style.removeProperty("--cwh-y");
      });
      document.querySelectorAll("[data-cwh-tilt]").forEach((card) => {
        card.style.removeProperty("--cwh-tilt-x");
        card.style.removeProperty("--cwh-tilt-y");
      });
    } else {
      initializeScrollMotion();
    }
  });

  const syncAllCampaignPins = () => {
    document
      .querySelectorAll(pinnedStageSelector)
      .forEach((campaign) => {
        const wrapper = campaign.parentElement;
        if (campaignPinQuery.matches) syncCampaignPin(campaign);
        else clearCampaignPin(wrapper);
      });
  };

  campaignPinQuery.addEventListener?.("change", syncAllCampaignPins);
  window.addEventListener("resize", syncAllCampaignPins, { passive: true });
})();
